import {
  createFeatureSelector,
  createSelector,
  DefaultProjectorFn,
  MemoizedSelector,
} from '@ngrx/store';
import { BaseEntityType } from './entity-actions-factory';
import { CaEntityState } from './entity-state';

/**
 * Generate the selectors for a slice of state.
 *
 * This is the modern typesafe equivalent to the selectors found in data-list.reducer.
 *
 * @param sliceName The slice of state to select
 * @returns The standard set of selectors
 */
export function caCreateEntitySelectors<
  EntityType extends BaseEntityType,
  StateType extends CaEntityState<EntityType>
>(sliceName: string) {
  const stateSelector = createFeatureSelector<StateType>(sliceName);
  const getIds = createSelector(stateSelector, (state: StateType) => state.ids);
  const getEntities = createSelector(stateSelector, (state: StateType) => state.entities);
  const getAll = createSelector(getIds, getEntities, (ids, entities) =>
    ids.map((id) => entities[id])
  );
  const getSelectedId = createSelector(stateSelector, (state) => state.selectedId);
  const getSelected = createSelector(
    getSelectedId,
    getEntities,
    (selectedId, entities) => entities[selectedId]
  );
  const getError = createSelector(stateSelector, (state) => state.error);

  const getLoadingEntities = createSelector(stateSelector, (state) => state.loadingEntities);
  const getSelectedIsLoading = createSelector(
    getLoadingEntities,
    getSelectedId,
    (entities, id) => !!entities[id]
  );
  const getLoadingAll = createSelector(stateSelector, (state: StateType) => state.loadingAll);
  const getCreating = createSelector(stateSelector, (state: StateType) => state.creating);
  const getPagination = createSelector(stateSelector, (state: StateType) => state.page);
  const getPaginators = createSelector(stateSelector, (state: StateType) => state.paginators);
  const getFilter = createSelector(stateSelector, (state: StateType) => state.filter);
  const getSearching = createSelector(stateSelector, (state: StateType) => state.searching);
  const getSearchResults = createSelector(stateSelector, (state: StateType) => state.searchResults);
  const getUpdatedAt = createSelector(stateSelector, (state: StateType) => state.updatedAt);

  const allPaginatedCache = {};
  const paginatedCompleteCache = {};
  const paginatedLoadingCache = {};
  const paginatedErrorCache = {};

  const getAllPaginated = (id: string = sliceName) => {
    const cache = allPaginatedCache;
    if (cache[id]) {
      return cache[id] as MemoizedSelector<object, EntityType[], DefaultProjectorFn<EntityType[]>>;
    }

    const selector = createSelector(getPaginators, getEntities, (paginators = {}, entities) => {
      const paginator = paginators[id];
      const paginatedIds = paginator && paginator.ids ? paginator.ids : [];
      return paginatedIds.map((id) => entities[id]).filter((entity) => !!entity);
    });
    cache[id] = selector;
    return cache[id] as MemoizedSelector<object, EntityType[], DefaultProjectorFn<EntityType[]>>;
  };

  const getAllPaginatedComplete = (id: string = sliceName) => {
    const cache = paginatedCompleteCache;
    if (cache[id]) {
      return cache[id] as MemoizedSelector<object, boolean, DefaultProjectorFn<boolean>>;
    }
    const selector = createSelector(getPaginators, (paginators) =>
      paginators && paginators[id] ? paginators[id].complete : false
    );
    cache[id] = selector;
    return cache[id] as MemoizedSelector<object, boolean, DefaultProjectorFn<boolean>>;
  };

  const isPaginatedLoading = (id: string = sliceName) => {
    const cache = paginatedLoadingCache;
    if (cache[id]) {
      return cache[id] as MemoizedSelector<object, boolean, DefaultProjectorFn<boolean>>;
    }
    cache[id] = createSelector(getPaginators, (paginators) =>
      paginators[id] ? paginators[id].loading : false
    );
    return cache[id] as MemoizedSelector<object, boolean, DefaultProjectorFn<boolean>>;
  };

  const isPaginatedError = (id: string = sliceName) => {
    const cache = paginatedErrorCache;
    if (cache[id]) {
      return cache[id] as MemoizedSelector<object, boolean, DefaultProjectorFn<boolean>>;
    }
    cache[id] = createSelector(getPaginators, (paginators) =>
      paginators[id] ? paginators[id].error : false
    );
    return cache[id] as MemoizedSelector<object, boolean, DefaultProjectorFn<boolean>>;
  };

  return {
    state: stateSelector,
    getIds,
    getEntities,
    getAll,
    getSelected,
    getSelectedId,
    getError,
    getLoadingEntities,
    getSelectedIsLoading,
    getLoadingAll,
    getCreating,
    getPagination,
    getPaginators,
    getFilter,
    getSearching,
    getSearchResults,
    getUpdatedAt,
    getAllPaginated,
    getAllPaginatedComplete,
    isPaginatedLoading,
    isPaginatedError,
  };
}
